import { db } from './schema'
import type {
  BodyRow,
  CycleRow,
  ProfileRow,
  RecoveryRow,
  SleepRow,
  WorkoutRow,
} from './schema'

export type DataKind = 'recovery' | 'cycles' | 'sleep' | 'workouts' | 'profile' | 'body'
export type Format = 'csv' | 'html' | 'markdown'
export type Range = 'week' | 'month' | 'year' | 'all'

export interface ExportOptions {
  kinds: DataKind[]
  format: Format
  range: Range
}

interface FlatTable {
  name: string
  fields: string[]
  rows: Record<string, unknown>[]
}

function isoDayOffset(daysBack: number): string {
  return new Date(Date.now() - daysBack * 86_400_000).toISOString().slice(0, 10)
}

function rangeStart(range: Range): string | null {
  switch (range) {
    case 'week':
      return isoDayOffset(7)
    case 'month':
      return isoDayOffset(30)
    case 'year':
      return isoDayOffset(365)
    case 'all':
      return null
  }
}

async function rowsForKind(kind: DataKind, since: string | null): Promise<FlatTable> {
  switch (kind) {
    case 'recovery':
      return flattenRecovery(await tableSince('recoveries', since))
    case 'cycles':
      return flattenCycles(await tableSince('cycles', since))
    case 'sleep':
      return flattenSleep(await tableSince('sleep', since))
    case 'workouts':
      return flattenWorkouts(await tableSince('workouts', since))
    case 'profile':
      return flattenProfile(await db.profile.get('me'))
    case 'body':
      return flattenBody(await db.body.get('me'))
  }
}

async function tableSince<K extends 'recoveries' | 'cycles' | 'sleep' | 'workouts'>(
  table: K,
  since: string | null,
): Promise<unknown[]> {
  const t = db[table] as unknown as { where: (k: string) => { aboveOrEqual: (s: string) => { toArray: () => Promise<unknown[]> } }; toArray: () => Promise<unknown[]> }
  if (since) return t.where('day').aboveOrEqual(since).toArray()
  return t.toArray()
}

// --- Per-kind flatteners (one row per record, sensible field order) ---

function flattenRecovery(rows: unknown[]): FlatTable {
  const data = rows as RecoveryRow[]
  return {
    name: 'recovery',
    fields: [
      'day',
      'cycle_id',
      'sleep_id',
      'recovery_score',
      'hrv_ms',
      'resting_hr',
      'spo2_pct',
      'skin_temp_c',
      'user_calibrating',
      'score_state',
      'created_at',
    ],
    rows: data
      .sort((a, b) => (a.day < b.day ? -1 : 1))
      .map((r) => ({
        day: r.day,
        cycle_id: r.cycle_id,
        sleep_id: r.sleep_id,
        recovery_score: r.score?.recovery_score ?? '',
        hrv_ms: r.score?.hrv_rmssd_milli?.toFixed(2) ?? '',
        resting_hr: r.score?.resting_heart_rate ?? '',
        spo2_pct: r.score?.spo2_percentage?.toFixed(2) ?? '',
        skin_temp_c: r.score?.skin_temp_celsius?.toFixed(2) ?? '',
        user_calibrating: r.score?.user_calibrating ?? '',
        score_state: r.score_state,
        created_at: r.created_at,
      })),
  }
}

function flattenCycles(rows: unknown[]): FlatTable {
  const data = rows as CycleRow[]
  return {
    name: 'cycles',
    fields: [
      'day',
      'cycle_id',
      'start',
      'end',
      'timezone_offset',
      'strain',
      'kilojoule',
      'avg_hr',
      'max_hr',
      'score_state',
    ],
    rows: data
      .sort((a, b) => (a.day < b.day ? -1 : 1))
      .map((c) => ({
        day: c.day,
        cycle_id: c.id,
        start: c.start,
        end: c.end ?? '',
        timezone_offset: c.timezone_offset,
        strain: c.score?.strain?.toFixed(3) ?? '',
        kilojoule: c.score?.kilojoule?.toFixed(0) ?? '',
        avg_hr: c.score?.average_heart_rate ?? '',
        max_hr: c.score?.max_heart_rate ?? '',
        score_state: c.score_state,
      })),
  }
}

function flattenSleep(rows: unknown[]): FlatTable {
  const data = rows as SleepRow[]
  return {
    name: 'sleep',
    fields: [
      'day',
      'sleep_id',
      'cycle_id',
      'start',
      'end',
      'timezone_offset',
      'nap',
      'duration_min',
      'asleep_min',
      'rem_min',
      'sws_min',
      'light_min',
      'awake_min',
      'cycles',
      'disturbances',
      'performance_pct',
      'consistency_pct',
      'efficiency_pct',
      'respiratory_rate',
      'sleep_baseline_min',
      'need_from_debt_min',
      'need_from_strain_min',
      'need_from_nap_min',
      'score_state',
    ],
    rows: data
      .sort((a, b) => (a.day < b.day ? -1 : 1))
      .map((s) => {
        const stages = s.score?.stage_summary
        const need = s.score?.sleep_needed
        const inBed = stages?.total_in_bed_time_milli ?? 0
        const awake = stages?.total_awake_time_milli ?? 0
        const noData = stages?.total_no_data_time_milli ?? 0
        const min = (ms: number | undefined) => (ms == null ? '' : Math.round(ms / 60_000))
        return {
          day: s.day,
          sleep_id: s.id,
          cycle_id: s.cycle_id ?? '',
          start: s.start,
          end: s.end,
          timezone_offset: s.timezone_offset,
          nap: s.nap,
          duration_min: min(inBed),
          asleep_min: min(inBed - awake - noData),
          rem_min: min(stages?.total_rem_sleep_time_milli),
          sws_min: min(stages?.total_slow_wave_sleep_time_milli),
          light_min: min(stages?.total_light_sleep_time_milli),
          awake_min: min(awake),
          cycles: stages?.sleep_cycle_count ?? '',
          disturbances: stages?.disturbance_count ?? '',
          performance_pct: s.score?.sleep_performance_percentage ?? '',
          consistency_pct: s.score?.sleep_consistency_percentage ?? '',
          efficiency_pct: s.score?.sleep_efficiency_percentage?.toFixed(2) ?? '',
          respiratory_rate: s.score?.respiratory_rate?.toFixed(2) ?? '',
          sleep_baseline_min: min(need?.baseline_milli),
          need_from_debt_min: min(need?.need_from_sleep_debt_milli),
          need_from_strain_min: min(need?.need_from_recent_strain_milli),
          need_from_nap_min: min(need?.need_from_recent_nap_milli),
          score_state: s.score_state,
        }
      }),
  }
}

function flattenWorkouts(rows: unknown[]): FlatTable {
  const data = rows as WorkoutRow[]
  return {
    name: 'workouts',
    fields: [
      'day',
      'workout_id',
      'sport_name',
      'start',
      'end',
      'timezone_offset',
      'duration_min',
      'strain',
      'avg_hr',
      'max_hr',
      'kilojoule',
      'distance_m',
      'altitude_gain_m',
      'altitude_change_m',
      'zone0_min',
      'zone1_min',
      'zone2_min',
      'zone3_min',
      'zone4_min',
      'zone5_min',
      'percent_recorded',
      'score_state',
    ],
    rows: data
      .sort((a, b) => (a.day < b.day ? -1 : 1))
      .map((w) => {
        const z = w.score?.zone_durations
        const min = (ms: number | undefined) => (ms == null ? '' : Math.round(ms / 60_000))
        const dur =
          w.start && w.end ? Math.round((+new Date(w.end) - +new Date(w.start)) / 60_000) : ''
        return {
          day: w.day,
          workout_id: w.id,
          sport_name: w.sport_name ?? '',
          start: w.start,
          end: w.end,
          timezone_offset: w.timezone_offset,
          duration_min: dur,
          strain: w.score?.strain?.toFixed(3) ?? '',
          avg_hr: w.score?.average_heart_rate ?? '',
          max_hr: w.score?.max_heart_rate ?? '',
          kilojoule: w.score?.kilojoule?.toFixed(0) ?? '',
          distance_m: w.score?.distance_meter ?? '',
          altitude_gain_m: w.score?.altitude_gain_meter ?? '',
          altitude_change_m: w.score?.altitude_change_meter ?? '',
          zone0_min: min(z?.zone_zero_milli),
          zone1_min: min(z?.zone_one_milli),
          zone2_min: min(z?.zone_two_milli),
          zone3_min: min(z?.zone_three_milli),
          zone4_min: min(z?.zone_four_milli),
          zone5_min: min(z?.zone_five_milli),
          percent_recorded: w.score?.percent_recorded ?? '',
          score_state: w.score_state,
        }
      }),
  }
}

function flattenProfile(p: ProfileRow | undefined): FlatTable {
  return {
    name: 'profile',
    fields: ['first_name', 'last_name', 'email', 'user_id'],
    rows: p
      ? [
          {
            first_name: p.first_name,
            last_name: p.last_name,
            email: p.email,
            user_id: p.user_id,
          },
        ]
      : [],
  }
}

function flattenBody(b: BodyRow | undefined): FlatTable {
  return {
    name: 'body',
    fields: ['height_meter', 'weight_kilogram', 'max_heart_rate'],
    rows: b
      ? [
          {
            height_meter: b.height_meter,
            weight_kilogram: b.weight_kilogram,
            max_heart_rate: b.max_heart_rate,
          },
        ]
      : [],
  }
}

// --- Format encoders ---

function csvEscape(v: unknown): string {
  if (v == null || v === '') return ''
  const s = String(v)
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"'
  return s
}

function tableToCSV(t: FlatTable): string {
  const header = t.fields.join(',')
  const body = t.rows.map((r) => t.fields.map((f) => csvEscape(r[f])).join(',')).join('\n')
  return header + '\n' + body + '\n'
}

function tableToMarkdown(t: FlatTable): string {
  const header = '| ' + t.fields.join(' | ') + ' |'
  const sep = '| ' + t.fields.map(() => '---').join(' | ') + ' |'
  const body = t.rows
    .map((r) => '| ' + t.fields.map((f) => mdCell(r[f])).join(' | ') + ' |')
    .join('\n')
  return `## ${t.name}\n\n${header}\n${sep}\n${body}\n`
}

function mdCell(v: unknown): string {
  if (v == null || v === '') return ' '
  return String(v).replace(/\|/g, '\\|').replace(/\n/g, ' ')
}

function htmlEscape(v: unknown): string {
  if (v == null) return ''
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function tableToHTML(t: FlatTable): string {
  const head = t.fields.map((f) => `<th>${htmlEscape(f)}</th>`).join('')
  const body = t.rows
    .map(
      (r) =>
        '<tr>' +
        t.fields.map((f) => `<td>${htmlEscape(r[f])}</td>`).join('') +
        '</tr>',
    )
    .join('\n')
  return `<section>
  <h2>${t.name} <small>(${t.rows.length})</small></h2>
  <div class="scroll"><table>
    <thead><tr>${head}</tr></thead>
    <tbody>
${body}
    </tbody>
  </table></div>
</section>`
}

const HTML_HEAD = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Whoop Lens — Export</title>
  <style>
    :root { color-scheme: light dark; }
    body { font: 14px/1.5 -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif; margin: 32px; max-width: 100%; }
    h1 { margin: 0 0 4px; }
    h2 { margin-top: 32px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    h2 small { color: #888; font-weight: 400; font-size: 12px; }
    .meta { color: #666; font-size: 12px; margin-bottom: 24px; }
    .scroll { overflow-x: auto; }
    table { border-collapse: collapse; font-size: 12px; }
    th, td { padding: 4px 10px; border-bottom: 1px solid #eee; text-align: left; white-space: nowrap; }
    th { background: #f6f6f6; position: sticky; top: 0; }
    tbody tr:hover { background: #fafafa; }
    @media (prefers-color-scheme: dark) {
      body { background: #0a0a0a; color: #e5e5e5; }
      h2 { border-color: #2a2a2a; }
      th, td { border-color: #1f1f1f; }
      th { background: #171717; }
      tbody tr:hover { background: #141414; }
    }
  </style>
</head>
<body>`

// --- Top-level builder ---

export interface ExportPreview {
  kinds: { kind: DataKind; rows: number }[]
  totalRows: number
}

export async function previewExport(opts: ExportOptions): Promise<ExportPreview> {
  const since = rangeStart(opts.range)
  const tables = await Promise.all(opts.kinds.map((k) => rowsForKind(k, since)))
  const detail = opts.kinds.map((kind, i) => ({ kind, rows: tables[i].rows.length }))
  return { kinds: detail, totalRows: detail.reduce((a, d) => a + d.rows, 0) }
}

export async function buildExport(
  opts: ExportOptions,
): Promise<{ filename: string; mime: string; content: string }[]> {
  const since = rangeStart(opts.range)
  const tables = await Promise.all(opts.kinds.map((k) => rowsForKind(k, since)))
  const stamp = new Date().toISOString().slice(0, 10)
  const slug = `whoop-lens-${opts.range}-${stamp}`
  const generated = `Generated by Whoop Lens · range: ${opts.range} · ${new Date().toLocaleString()}`

  if (opts.format === 'csv') {
    // One CSV per data kind, since CSV doesn't compose well across schemas.
    return tables.map((t) => ({
      filename: `${slug}-${t.name}.csv`,
      mime: 'text/csv;charset=utf-8',
      content: tableToCSV(t),
    }))
  }

  if (opts.format === 'markdown') {
    const sections = tables.map(tableToMarkdown).join('\n')
    const header = `# Whoop Lens export\n\n_${generated}_\n\n`
    return [
      {
        filename: `${slug}.md`,
        mime: 'text/markdown;charset=utf-8',
        content: header + sections,
      },
    ]
  }

  // HTML
  const sections = tables.map(tableToHTML).join('\n')
  const html = `${HTML_HEAD}
  <h1>Whoop Lens export</h1>
  <div class="meta">${htmlEscape(generated)}</div>
  ${sections}
</body></html>`
  return [
    {
      filename: `${slug}.html`,
      mime: 'text/html;charset=utf-8',
      content: html,
    },
  ]
}

export function downloadFile(name: string, mime: string, content: string): void {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Defer revoke so the download has time to start.
  setTimeout(() => URL.revokeObjectURL(url), 1500)
}
